import { z } from 'zod';
import { ADD_ON_MODULES, MODULE_CODES } from '../modules';
import { ENTITLEMENT_TOGGLE } from '../enums';
import { totpCodeSchema } from './owner';
import { FREEZE_CATEGORIES, SUSPEND_CATEGORIES, SUSPEND_MODES, TENANT_ENVIRONMENTS } from '../tenant-lifecycle';
import { createPlatformUserSchema, createTenantSchema, planLimitsSchema, resetPlatformUserPasswordSchema, updatePlatformUserSchema, upsertPlanSchema, } from './platform';
const ownerReasonSchema = z.string().trim().min(5).max(400);
const stepUp = {
    totpCode: totpCodeSchema,
    reason: ownerReasonSchema,
};
export const ownerCreateTenantSchema = createTenantSchema.omit({ country: true, currency: true }).extend({
    ...stepUp,
    environment: z.enum(TENANT_ENVIRONMENTS),
    offlineEnabled: z.boolean().default(false),
    requestId: z.string().uuid().optional(),
});
export const ownerSetEntitlementSchema = z.object({
    ...stepUp,
    moduleCode: z.enum(MODULE_CODES),
    status: z.enum(ENTITLEMENT_TOGGLE),
});
export const ownerBulkEntitlementSchema = z.object({
    ...stepUp,
    entitlements: z
        .array(z.object({ moduleCode: z.enum(MODULE_CODES), status: z.enum(ENTITLEMENT_TOGGLE) }))
        .min(1)
        .max(MODULE_CODES.length),
});
export const ownerUpsertPlanSchema = upsertPlanSchema.extend(stepUp);
export const ownerSuspendTenantSchema = z.object({
    ...stepUp,
    readOnlyGrace: z.boolean().default(true),
    mode: z.enum(SUSPEND_MODES).optional(),
    category: z.enum(SUSPEND_CATEGORIES).default('other'),
    confirmSlug: z.string().trim().max(80).optional(),
});
export const ownerFreezeTenantSchema = z.object({
    ...stepUp,
    category: z.enum(FREEZE_CATEGORIES),
});
export const ownerTenantEnvironmentSchema = z.object({
    ...stepUp,
    environment: z.enum(TENANT_ENVIRONMENTS),
});
export const FREEZES_PER_ADMIN_PER_DAY = 3;
export const ownerTenantLicenceSchema = z.object({
    ...stepUp,
    planIds: z.array(z.string().uuid()).min(1).max(4),
    addOns: z.array(z.enum(ADD_ON_MODULES)).max(ADD_ON_MODULES.length).default([]),
    overrides: z
        .array(z.object({ moduleCode: z.enum(MODULE_CODES), status: z.enum(ENTITLEMENT_TOGGLE) }))
        .max(MODULE_CODES.length)
        .optional(),
});
const UUID_LIST = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(,[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}){0,3}$/i;
const ADD_ON_LIST = new RegExp(`^((${ADD_ON_MODULES.join('|')})(,(${ADD_ON_MODULES.join('|')}))*)?$`);
export const ownerLicencePreviewQuerySchema = z.object({
    planIds: z.string().max(200).regex(UUID_LIST, 'Pick one to four packages'),
    addOns: z.string().max(200).regex(ADD_ON_LIST, 'Unknown add-on').optional(),
});
export function licencePreviewSelection(query) {
    const split = (value) => (value ?? '').split(',').map((part) => part.trim()).filter((part) => part.length > 0);
    return {
        planIds: [...new Set(split(query.planIds))],
        addOns: [...new Set(split(query.addOns))].filter((code) => ADD_ON_MODULES.includes(code)),
    };
}
export const ownerTenantCommercialSchema = z
    .object({
    ...stepUp,
    priceMinor: z.number().int().min(0).optional(),
    billingCycle: z.enum(['monthly', 'annual']).optional(),
    setupFeeMinor: z.number().int().min(0).optional(),
    trialEndsAt: z.string().datetime().nullable().optional(),
})
    .refine((body) => body.priceMinor !== undefined ||
    body.billingCycle !== undefined ||
    body.setupFeeMinor !== undefined ||
    body.trialEndsAt !== undefined, 'nothing to update');
export const ownerTenantLimitsSchema = z.object({
    ...stepUp,
    limits: planLimitsSchema,
});
export const ownerTenantSharingSchema = z
    .object({
    ...stepUp,
    shareHistoryWithinGroup: z.boolean().optional(),
    shareHistoryAcrossPlatform: z.boolean().optional(),
})
    .refine((body) => body.shareHistoryWithinGroup !== undefined || body.shareHistoryAcrossPlatform !== undefined, 'nothing to update');
export const ownerCreateStaffSchema = createPlatformUserSchema.extend(stepUp);
export const ownerUpdateStaffSchema = updatePlatformUserSchema.extend(stepUp);
export const ownerResetStaffPasswordSchema = resetPlatformUserPasswordSchema.extend(stepUp);
export const ownerTenantProfileSchema = z.object({
    ...stepUp,
    name: z.string().trim().min(2).max(160).optional(),
    customDomain: z.string().trim().max(190).nullable().optional(),
    locale: z.string().min(2).max(10).optional(),
});
export const GRACE_EXTENSION_MAX_DAYS = 30;
export const GRACE_CODE_MAX_DAYS = 7;
export const GRACE_CODE_EVERY_DAYS = 30;
export const GRACE_CODE_DOMAIN = 'his-grace-v1\n';
export const ownerGraceExtensionSchema = z.object({
    ...stepUp,
    until: z.string().datetime().nullable(),
});
export const ownerGraceCodeSchema = z.object({
    ...stepUp,
    days: z.number().int().min(1).max(GRACE_CODE_MAX_DAYS),
});
export const localGraceCodeSchema = z.object({
    code: z.string().trim().min(20).max(2000),
});
export const TENANT_EXPORT_STATUSES = ['queued', 'running', 'ready', 'downloaded', 'failed', 'expired', 'cancelled'];
export const ownerExportActionSchema = z.object({ ...stepUp });
export const ownerExportDeliveredSchema = z.object({
    ...stepUp,
    reference: z.string().trim().min(3).max(200),
});
export const ownerExportWaiverSchema = z.object({
    ...stepUp,
    waiver: z.string().trim().min(20).max(2000),
    attachmentReference: z.string().trim().min(3).max(300),
});
export const requestDataExportSchema = z.object({
    totpCode: totpCodeSchema,
});
export const CLOSURE_NOTICE_DAYS = 30;
export const DELETION_GRACE_DAYS = 7;
export const DEMO_DELETION_GRACE_HOURS = 24;
export const PURGE_SIZE_GUARD_ROWS = 200_000;
export const ownerScheduleClosureSchema = z.object({
    totpCode: totpCodeSchema,
    reason: z.string().trim().min(10).max(400),
    noticeDays: z.number().int().min(1).max(365).default(CLOSURE_NOTICE_DAYS),
    graceDays: z.number().int().min(1).max(60).default(DELETION_GRACE_DAYS),
    access: z.enum(['everyone', 'admins']).default('everyone'),
    confirmSlug: z.string().trim().max(80),
});
export const ownerCancelClosureSchema = z.object({ ...stepUp });
export const ownerReleaseExportSchema = z.object({ ...stepUp });
//# sourceMappingURL=owner-licence.js.map